import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wallet, Loader2, Save } from "lucide-react";

export default function Caparra() {
  const [base, setBase] = useState(null);
  const [caparra, setCaparra] = useState({ caparra_percentuale: 10, caparra_min: 1500, caparra_max: 3500 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/settings/condizioni")
      .then((r) => {
        setBase(r.data);
        setCaparra({ caparra_percentuale: r.data.caparra_percentuale ?? 10, caparra_min: r.data.caparra_min ?? 1500, caparra_max: r.data.caparra_max ?? 3500 });
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const cap = {
        caparra_percentuale: Number(caparra.caparra_percentuale) || 0,
        caparra_min: Number(caparra.caparra_min) || 0,
        caparra_max: Number(caparra.caparra_max) || 0,
      };
      const { data } = await api.put("/settings/condizioni", { ...base, ...cap });
      setBase(data);
      setCaparra({ caparra_percentuale: data.caparra_percentuale ?? 10, caparra_min: data.caparra_min ?? 1500, caparra_max: data.caparra_max ?? 3500 });
      toast.success("Caparra salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="caparra-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><Wallet className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-xl font-heading font-bold">Caparra</h1>
            <p className="text-xs text-muted-foreground">Caparra da versare all'ordine</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-caparra-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      </header>

      <div className="p-4 lg:p-8 max-w-4xl">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-4">La caparra da versare all'ordine è calcolata come <b>percentuale dell'importo dell'impianto (IVA incl.)</b>, con un valore minimo e uno massimo. Default: <b>10%</b>, min <b>1.500 €</b>, max <b>3.500 €</b>.</p>
            <Card className="rounded-lg border-border p-5 max-w-xl">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Percentuale (%)</Label>
                  <Input type="number" min="0" step="0.5" value={caparra.caparra_percentuale} onChange={(e) => setCaparra((p) => ({ ...p, caparra_percentuale: e.target.value === "" ? "" : Number(e.target.value) }))} data-testid="caparra-percentuale-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Minimo (€)</Label>
                  <Input type="number" min="0" step="50" value={caparra.caparra_min} onChange={(e) => setCaparra((p) => ({ ...p, caparra_min: e.target.value === "" ? "" : Number(e.target.value) }))} data-testid="caparra-min-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Massimo (€)</Label>
                  <Input type="number" min="0" step="50" value={caparra.caparra_max} onChange={(e) => setCaparra((p) => ({ ...p, caparra_max: e.target.value === "" ? "" : Number(e.target.value) }))} data-testid="caparra-max-input" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">Esempio: impianto da <b>25.000 €</b> → 10% = 2.500 € (entro min/max) → caparra <b>2.500 €</b>. Impianto da <b>12.000 €</b> → 10% = 1.200 € → sotto il minimo → caparra <b>1.500 €</b>. Impianto da <b>50.000 €</b> → 10% = 5.000 € → oltre il massimo → caparra <b>3.500 €</b>.</p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
